import React from 'react';
import { Building2, AlertCircle } from 'lucide-react';
import { useCompany } from '../../hooks/useCompany';
import CompanyOnboarding from './CompanyOnboarding';
import CompanyTabs from './CompanyTabs';

/**
 * Company Component - Main entry point
 * Determines whether to show onboarding or dashboard based on company existence
 * 
 * Enhanced with:
 * - Improved loading states with better visual feedback
 * - Professional error states with retry functionality
 * - Consistent styling with the rest of the application
 */
const Company = () => {
  const { hasCompany, loading, error, checkCompanyStatus, createCompany } = useCompany();

  // Loading state - Enhanced
  if (loading) {
    return (
      <div className="max-w-[2000px] w-full mx-auto px-6">
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
              <Building2 className="h-6 w-6 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-gray-600 font-medium">Loading company information...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state - Enhanced
  if (error) {
    return (
      <div className="max-w-[2000px] w-full mx-auto px-6">
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="bg-red-50 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => checkCompanyStatus()}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Route to appropriate component based on company status
  if (hasCompany) {
    return <CompanyTabs />;
  } else {
    return <CompanyOnboarding createCompany={createCompany} loading={loading} />;
  }
};

export default Company;